/**
 * TEMPLATE GALLERY DATA
 * Each entry maps to one of the 3 real backend templates (`classic`, `modern`,
 * `royal`) with a community + accent overlay so we can show a scrollable
 * gallery of 50+ variants without duplicating server-side files.
 */

const BASE_TEMPLATES = [
  {
    base: "classic",
    baseTitle: "Classic Traditional",
    subtitle: "Serif · Om header · maroon border",
    style: "Serif",
  },
  {
    base: "modern",
    baseTitle: "Modern Minimal",
    subtitle: "Sans-serif · photo-forward · airy",
    style: "Minimal",
  },
  {
    base: "royal",
    baseTitle: "Royal Ornate",
    subtitle: "Gold gradient · dual-column · regal",
    style: "Ornate",
  },
];

/** Community and regional variants — each carries a hero motif and accent. */
const COMMUNITIES = [
  { key: "Hindu", label: "Hindu", motif: "ॐ", accent: "#c1272d", tint: "#fdecec" },
  { key: "Muslim", label: "Muslim", motif: "☪", accent: "#0f766e", tint: "#e8f7f3" },
  { key: "Christian", label: "Christian", motif: "✚", accent: "#1e40af", tint: "#e8effa" },
  { key: "Sikh", label: "Sikh", motif: "☬", accent: "#ea7a1a", tint: "#fef3e6" },
  { key: "Jain", label: "Jain", motif: "卐", accent: "#a16207", tint: "#fef6d9" },
  { key: "Buddhist", label: "Buddhist", motif: "☸", accent: "#b45309", tint: "#fff2df" },
  { key: "Parsi", label: "Parsi", motif: "✷", accent: "#b91c1c", tint: "#fdecec" },
  { key: "Marathi", label: "Marathi", motif: "श्री", accent: "#7c2d12", tint: "#fbeee6" },
  { key: "Tamil", label: "Tamil", motif: "ஶ்ரீ", accent: "#065f46", tint: "#e5f5ef" },
  { key: "Telugu", label: "Telugu", motif: "శ్రీ", accent: "#4c1d95", tint: "#efeafc" },
  { key: "Kannada", label: "Kannada", motif: "ಶ್ರೀ", accent: "#7e22ce", tint: "#f3e8fd" },
  { key: "Malayali", label: "Malayali", motif: "ശ്രീ", accent: "#0e7490", tint: "#e0f2f7" },
  { key: "Bengali", label: "Bengali", motif: "শ্রী", accent: "#be185d", tint: "#fbe6ee" },
  { key: "Gujarati", label: "Gujarati", motif: "श्री", accent: "#c2410c", tint: "#feede0" },
  { key: "Punjabi", label: "Punjabi", motif: "☬", accent: "#d97706", tint: "#fef3d9" },
  { key: "Odia", label: "Odia", motif: "ଓ", accent: "#166534", tint: "#e6f5ea" },
  { key: "Assamese", label: "Assamese", motif: "শ্ৰী", accent: "#155e75", tint: "#e0f3f7" },
  { key: "Kashmiri", label: "Kashmiri", motif: "❄", accent: "#1d4ed8", tint: "#e3ebfb" },
];

/**
 * Expand base × community into 54 template variants.
 * Each variant has a stable id, tags, and the info needed to render a
 * gallery card preview + a launch link into the builder.
 */
export const TEMPLATE_GALLERY = BASE_TEMPLATES.flatMap((b) =>
  COMMUNITIES.map((c) => ({
    id: `${b.base}-${c.key.toLowerCase()}`,
    template: b.base,
    community: c.key,
    title: `${b.baseTitle} · ${c.label}`,
    subtitle: b.subtitle,
    style: b.style,
    accent: c.accent,
    tint: c.tint,
    motif: c.motif,
    tags: [b.baseTitle, c.label],
    popular: c.key === "Hindu" && b.base === "classic",
    isNew: c.key === "Buddhist" && b.base === "modern",
  })),
);

export const TEMPLATE_STYLES = [
  { id: "all", label: "All templates" },
  { id: "classic", label: "Classic" },
  { id: "modern", label: "Modern" },
  { id: "royal", label: "Royal" },
];

export const TEMPLATE_COMMUNITIES = [
  "All",
  ...COMMUNITIES.map((c) => c.key),
];

/** Look up a variant by id — used by the builder to seed template + religion. */
export function findVariant(id) {
  return TEMPLATE_GALLERY.find((t) => t.id === id) || null;
}
